import type React from 'react'
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { autocompleteAddress, addressMetadata } from './api'
import type { AddressCompletion, AddressFields } from './types'
import { Input } from '../ui/Input'
import { Button } from '../ui/Button'
import { Select } from '../ui/Select'

/**
 * ARIA 1.2 combobox pattern (https://www.w3.org/WAI/ARIA/apg/patterns/combobox/).
 * Radix doesn't ship a combobox primitive, so we hand-roll the ARIA wiring
 * over a plain <input> + listbox. Keyboard nav:
 *   ↓ / ↑    Move active option
 *   Enter    Select active option
 *   Escape   Close listbox; second press clears input
 *   Tab      Close listbox, keep typed text
 *
 * Selection flow:
 *   1. User types ≥ 3 chars → debounced autocomplete call
 *   2. User picks → addressMetadata() resolves the canonical record
 *   3. onChange fires with the structured fields
 *
 * Manual fallback:
 *   When the user clicks "Address not found", autocomplete is suppressed and
 *   the parent reveals plain inputs. `onModeChange` lets the parent react.
 */

export type AddressAutocompleteProps = {
  label?: string
  /**
   * Hide the label visually while keeping it accessible to screen readers
   * (applies the `visuallyhidden` utility class). Use when the surrounding
   * UI already makes the field's purpose obvious to sighted users.
   */
  hideLabel?: boolean
  placeholder?: string
  /** Initial display value (e.g. when re-hydrating a saved address). */
  defaultValue?: string
  /** Fires when a completion is picked AND metadata resolved. */
  onChange: (fields: AddressFields) => void
  /** Fires when the user toggles into manual entry. */
  onModeChange?: (mode: 'autocomplete' | 'manual') => void
  /**
   * Controlled entry mode. When provided, the parent owns the
   * autocomplete/manual toggle — pair it with `onModeChange` so a trigger
   * elsewhere on the page (e.g. a "enter your address manually" link in the
   * intro copy) can flip this into manual entry. Leave undefined to let the
   * component manage the mode internally via its own fallback link.
   */
  mode?: 'autocomplete' | 'manual'
  /** Override the debounce window (default 250ms). */
  debounceMs?: number
  /** Minimum chars before firing autocomplete. Default 3. */
  minChars?: number
  /** Disable the manual fallback link entirely. */
  hideManualFallback?: boolean
  /**
   * Which number slot the manual form starts on ('house' street number, or
   * 'lot' for new-build lot searches). Applied on mount and whenever the
   * parent flips `mode` into 'manual', so a "search by lot number" trigger on
   * the page can drop the user straight onto the Lot toggle. The user can
   * still switch it afterwards. Defaults to 'house'.
   */
  manualNumberType?: 'house' | 'lot'
}

export function AddressAutocomplete({
  label = 'Address',
  hideLabel,
  placeholder = 'Start typing your address…',
  defaultValue = '',
  onChange,
  onModeChange,
  debounceMs = 250,
  minChars = 3,
  hideManualFallback,
  mode: controlledMode,
  manualNumberType,
}: AddressAutocompleteProps) {
  const reactId = useId()
  const inputId = `${reactId}-input`
  const listId = `${reactId}-list`

  const [query, setQuery] = useState(defaultValue)
  const [completions, setCompletions] = useState<AddressCompletion[]>([])
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // `mode` is controllable: a `mode` prop from the parent wins (so a link
  // elsewhere on the page can flip us into manual entry); otherwise we own it
  // internally via the "Enter it manually" fallback link. setMode still
  // updates the internal value when controlled — harmlessly ignored — and the
  // onModeChange calls keep a controlling parent in sync.
  const [internalMode, setMode] = useState<'autocomplete' | 'manual'>('autocomplete')
  const mode = controlledMode ?? internalMode
  // Manual entry mirrors the legacy AdvancedSearchForm (al_screens) so users
  // with new-build properties (no street number yet) can enter a lot number
  // instead. `numberType` flips which slot the typed number lands in.
  const [manual, setManual] = useState({
    unit: '',
    number: '',
    numberType: manualNumberType ?? ('house' as 'house' | 'lot'),
    street: '',
    city: '',
    state: '',
    postcode: '',
  })
  const [manualError, setManualError] = useState<string | null>(null)

  // Seed the Street/Lot toggle from `manualNumberType` whenever the parent
  // flips us into manual entry (e.g. a "search by lot number" button on the
  // page). Only fires on the transition, so it never fights a user who then
  // toggles it themselves.
  const prevModeRef = useRef(controlledMode ?? internalMode)
  useEffect(() => {
    const m = controlledMode ?? internalMode
    if (prevModeRef.current !== 'manual' && m === 'manual' && manualNumberType) {
      setManual((p) => ({ ...p, numberType: manualNumberType }))
    }
    prevModeRef.current = m
  }, [controlledMode, internalMode, manualNumberType])

  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const requestSeqRef = useRef(0)

  // Close on outside click.
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!containerRef.current) return
      if (!containerRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [])

  const runSearch = useCallback(
    async (q: string) => {
      const trimmed = q.trim()
      if (trimmed.length < minChars) {
        setCompletions([])
        setOpen(false)
        setLoading(false)
        return
      }
      const seq = ++requestSeqRef.current
      setLoading(true)
      setError(null)
      try {
        const results = await autocompleteAddress(trimmed)
        if (seq !== requestSeqRef.current) return // stale request
        setCompletions(results)
        setActiveIndex(results.length > 0 ? 0 : -1)
        setOpen(true)
      } catch (err) {
        if (seq !== requestSeqRef.current) return
        setError(extractMessage(err))
        setCompletions([])
        setOpen(true) // open to show the error
      } finally {
        if (seq === requestSeqRef.current) setLoading(false)
      }
    },
    [minChars],
  )

  const handleInput = useCallback(
    (next: string) => {
      setQuery(next)
      if (mode === 'manual') return
      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => runSearch(next), debounceMs)
    },
    [debounceMs, mode, runSearch],
  )

  const selectCompletion = useCallback(
    async (c: AddressCompletion) => {
      setQuery(c.full_address)
      setOpen(false)
      setLoading(true)
      setError(null)
      try {
        const meta = await addressMetadata(c.id)
        onChange({
          street: composeStreet(meta),
          unit: meta.unit_identifier ?? '',
          city: meta.locality_name ?? '',
          state: meta.state_territory ?? '',
          postcode: meta.postcode ?? '',
          canonicalAddressId: c.canonical_address_id ?? c.id,
        })
      } catch (err) {
        setError(extractMessage(err))
      } finally {
        setLoading(false)
      }
    },
    [onChange],
  )

  const handleKey = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
        if (completions.length > 0) {
          setOpen(true)
          setActiveIndex(0)
          e.preventDefault()
          return
        }
      }
      if (!open) return

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setActiveIndex((i) => (i + 1) % Math.max(completions.length, 1))
          break
        case 'ArrowUp':
          e.preventDefault()
          setActiveIndex((i) => (i - 1 + completions.length) % Math.max(completions.length, 1))
          break
        case 'Enter': {
          if (activeIndex >= 0 && completions[activeIndex]) {
            e.preventDefault()
            void selectCompletion(completions[activeIndex])
          }
          break
        }
        case 'Escape':
          e.preventDefault()
          if (open) setOpen(false)
          else setQuery('')
          break
        case 'Tab':
          setOpen(false)
          break
        default:
          break
      }
    },
    [activeIndex, completions, open, selectCompletion],
  )

  const switchToManual = useCallback(() => {
    setMode('manual')
    setOpen(false)
    setCompletions([])
    onModeChange?.('manual')
  }, [onModeChange])

  const switchToAutocomplete = useCallback(() => {
    setMode('autocomplete')
    onModeChange?.('autocomplete')
  }, [onModeChange])

  const activeOptionId = useMemo(
    () => (activeIndex >= 0 ? `${listId}-opt-${activeIndex}` : undefined),
    [activeIndex, listId],
  )

  if (mode === 'manual') {
    const submitManual = (e: React.FormEvent) => {
      e.preventDefault()
      const number = manual.number.trim()
      const street = manual.street.trim()
      const city = manual.city.trim()
      const state = manual.state.trim()
      if (!number || !street || !city || !state) {
        setManualError('Please fill out the compulsory fields.')
        return
      }
      setManualError(null)
      // Mirror legacy normalisation: "Unit 5" → "5".
      const unit = manual.unit.trim().toLowerCase().replace('unit', '').replace(/\s+/g, '')
      onChange({
        street,
        unit,
        city,
        state,
        postcode: manual.postcode.trim(),
        lot: manual.numberType === 'lot' ? number : undefined,
        houseNo: manual.numberType === 'house' ? number : undefined,
      })
    }
    return (
      <form className="al-address al-address--manual" onSubmit={submitManual} noValidate>
        <p className="al-address__manual-note xsmall dark-grey m-bx3">
          Enter your address below.{' '}
          <button type="button" className="al-address__link" onClick={switchToAutocomplete}>
            Switch back to address search
          </button>
        </p>
        <Input
          label="Unit number"
          className="input-text"
          value={manual.unit}
          onChange={(e) => setManual((p) => ({ ...p, unit: e.target.value }))}
        />
        {/* Inlined rather than using the shared <Input> because the toggle
            sits as a flex sibling to the <input> — that's what keeps their
            heights in sync via `align-items: stretch`, regardless of how
            .input-text padding/font evolves. */}
        <div className="m-bx3 al-field">
          <label htmlFor={`${reactId}-num`} className="al-field__label">
            {manual.numberType === 'lot' ? 'Lot number' : 'Street number'}{' '}
            <span className="al-field__required" aria-hidden="true">
              *
            </span>
          </label>
          <div className="al-address__number-row">
            <input
              id={`${reactId}-num`}
              type="text"
              className="input-text"
              aria-required="true"
              value={manual.number}
              onChange={(e) => setManual((p) => ({ ...p, number: e.target.value }))}
            />
            <div className="al-address__number-toggle" role="group" aria-label="Number type">
              <button
                type="button"
                className={`al-address__toggle-btn ${manual.numberType === 'house' ? 'al-address__toggle-btn--active' : ''}`}
                aria-pressed={manual.numberType === 'house'}
                onClick={() => setManual((p) => ({ ...p, numberType: 'house' }))}
              >
                Street
              </button>
              <button
                type="button"
                className={`al-address__toggle-btn ${manual.numberType === 'lot' ? 'al-address__toggle-btn--active' : ''}`}
                aria-pressed={manual.numberType === 'lot'}
                onClick={() => setManual((p) => ({ ...p, numberType: 'lot' }))}
              >
                Lot
              </button>
            </div>
          </div>
        </div>
        <Input
          label="Street name"
          required
          autoComplete="address-line1"
          className="input-text"
          value={manual.street}
          onChange={(e) => setManual((p) => ({ ...p, street: e.target.value }))}
        />
        <Input
          label="Suburb"
          required
          autoComplete="address-level2"
          className="input-text"
          value={manual.city}
          onChange={(e) => setManual((p) => ({ ...p, city: e.target.value }))}
        />
        <div className="row">
          <div className="col-xs-4">
            <Select
              label="State"
              required
              value={manual.state}
              onChange={(e) => setManual((p) => ({ ...p, state: e.target.value }))}
            >
              <option value="">Select a state</option>
              <option value="VIC">VIC</option>
              <option value="QLD">QLD</option>
              <option value="NSW">NSW</option>
              <option value="ACT">ACT</option>
              <option value="SA" disabled>
                SA — Service Unavailable
              </option>
              <option value="TAS" disabled>
                TAS — Service Unavailable
              </option>
              <option value="WA" disabled>
                WA — Service Unavailable
              </option>
              <option value="NT" disabled>
                NT — Service Unavailable
              </option>
            </Select>
          </div>
          <div className="col-xs-4">
            <Input
              label="Postcode"
              inputMode="numeric"
              autoComplete="postal-code"
              className="input-text"
              value={manual.postcode}
              onChange={(e) => setManual((p) => ({ ...p, postcode: e.target.value }))}
            />
          </div>
        </div>

        {manualError ? (
          <p role="alert" className="al-address__error">
            {manualError}
          </p>
        ) : null}
        <Button type="submit" variant="primary">
          Continue
        </Button>
      </form>
    )
  }

  return (
    <div className="al-address" ref={containerRef}>
      <label
        htmlFor={inputId}
        // Apply both utility classes when hidden: `al-sr-only` ships with
        // shared/a11y.scss and works standalone; `visuallyhidden` is the WP
        // theme's equivalent and keeps parity with theme markup.
        className={hideLabel ? 'al-sr-only visuallyhidden' : 'al-field__label'}
      >
        {label}
      </label>
      <div className="al-address__combobox">
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          placeholder={placeholder}
          value={query}
          autoComplete="off"
          className="input-text"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={listId}
          aria-activedescendant={activeOptionId}
          onChange={(e) => handleInput(e.target.value)}
          onFocus={() => completions.length > 0 && setOpen(true)}
          onKeyDown={handleKey}
        />
        {loading ? (
          <span className="al-address__status" aria-live="polite">
            Searching…
          </span>
        ) : null}
      </div>

      <ul
        id={listId}
        role="listbox"
        className={`al-address__listbox ${open ? 'al-address__listbox--open' : ''}`}
        aria-label="Address suggestions"
      >
        {error ? (
          <li role="alert" className="al-address__error">
            {error}
          </li>
        ) : null}
        {!error &&
        open &&
        completions.length === 0 &&
        !loading &&
        query.trim().length >= minChars ? (
          <li role="status" className="al-address__empty">
            No matches found.
          </li>
        ) : null}
        {completions.map((c, idx) => {
          const id = `${listId}-opt-${idx}`
          const active = idx === activeIndex
          return (
            <li
              key={c.id || idx}
              id={id}
              role="option"
              aria-selected={active}
              className={`al-address__option ${active ? 'al-address__option--active' : ''}`}
              onMouseEnter={() => setActiveIndex(idx)}
              onMouseDown={(e) => {
                // mousedown (not click) so the input doesn't blur first.
                e.preventDefault()
                void selectCompletion(c)
              }}
            >
              {c.full_address}
            </li>
          )
        })}
      </ul>

      {!hideManualFallback ? (
        <p className="al-address__fallback xsmall dark-grey m-tx1">
          Can&apos;t find your address?{' '}
          <button type="button" className="al-address__link" onClick={switchToManual}>
            Enter it manually
          </button>
        </p>
      ) : null}
    </div>
  )
}

function composeStreet(meta: {
  address_line_1: string | null
  address_line_2: string | null
}): string {
  return [meta.address_line_1, meta.address_line_2].filter(Boolean).join(' ').trim()
}

function extractMessage(err: unknown): string {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown }
    if (typeof e.message === 'string') return e.message
  }
  return 'Address lookup failed.'
}
